import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
const DVLTooltip = ({ id, children, toggleContent, lang: propLang, dataH = "rightedge", dataV = "bottom", dataDigId, white }) => {
    let lang = propLang || document.documentElement.lang;
    if (lang === "fr-CA" || lang === "fr")
        lang = "fr";
    else
        lang = "en";
    const checkIsMobile = () => {
        return window.innerWidth < 640;
    };
    const focus = (el, delay) => {
        if (!el)
            return;
        if (window.innerWidth < 640) {
            document.activeElement?.blur();
            setTimeout(() => {
                el.focus();
            }, delay || 800);
        }
        else {
            if (delay) {
                setTimeout(() => {
                    el.focus();
                }, delay || 0);
            }
            else {
                el.focus();
            }
        }
    };
    const triggerRef = useRef(null);
    const tooltipWprRef = useRef(null);
    const tooltipRef = useRef(null);
    const tooltipInnerRef = useRef(null);
    const [isVisible, setVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(checkIsMobile());
    const previousState = useRef({ isVisible }).current;
    let posStyle = {
        top: -9999,
        left: -9999
    };
    let firstFocusable = null;
    let lastFocusable = null;
    if (isVisible && triggerRef.current && tooltipWprRef.current) {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft;
        const clientTop = document.documentElement.clientTop || document.body.clientTop || 0;
        const clientLeft = document.documentElement.clientLeft || document.body.clientLeft || 0;
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const triggerTop = triggerRect.top + scrollTop - clientTop;
        const triggerBottom = triggerTop + triggerRef.current.offsetHeight;
        const tooltipWidth = tooltipWprRef.current.offsetWidth;
        const tooltipHeight = tooltipWprRef.current.offsetHeight;
        const triggerLeft = triggerRect.left + scrollLeft - clientLeft;
        const triggerRight = triggerLeft + triggerRef.current.offsetWidth;
        let top = 0, left = 0;
        switch (dataH) {
            case "leftedge":
                left = triggerRight - 20;
                break;
            case "left":
                left = triggerRight - tooltipWidth - 40;
                break;
            case "rightedge":
                left = triggerRight - tooltipWidth;
                break;
            case "right":
                left = triggerLeft + 30;
                break;
        }
        switch (dataV) {
            case "bottom":
                top = triggerBottom;
                break;
            case "top":
                top = triggerTop - tooltipHeight;
                break;
            case "topedge":
                top = triggerTop;
                break;
            case "bottomedge":
                top = triggerBottom - tooltipHeight + 25;
                break;
        }
        posStyle = { top, left };
    }
    const tooltipWrapperStyle = {
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? 'visible' : 'hidden',
        position: 'absolute',
        zIndex: 100001
    };
    const tooltipStyle = {
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? 'visible' : 'hidden',
        pointerEvents: 'auto',
        display: 'inline-block',
        outline: 'none',
        zIndex: 999
    };
    const overlayStyle = {
        visibility: isVisible && window.innerWidth <= 666 ? 'visible' : 'hidden',
        backgroundColor: "rgba(56,56,56,.8)",
        bottom: 0,
        left: 0,
        opacity: isVisible && window.innerWidth <= 666 ? 1 : 0,
        position: "fixed",
        right: 0,
        top: 0,
        transition: "opacity .2s",
        zIndex: 0
    };
    useEffect(() => {
        const resize = () => {
            if (isVisible)
                setVisible(false);
            setIsMobile(checkIsMobile());
        };
        window.addEventListener('resize', resize);
        return () => {
            window.removeEventListener('resize', resize);
        };
    }, [isVisible]);
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (tooltipWprRef.current && !tooltipWprRef.current.contains(e.target)) {
                setVisible(false);
            }
        };
        const onEsc = (e) => {
            if (e.keyCode === 27) {
                setVisible(false);
            }
        };
        const onFocusOut = () => {
            setTimeout(() => {
                if (tooltipRef.current && !tooltipRef.current.contains(document.activeElement)) {
                    const pos = tooltipRef.current.compareDocumentPosition(document.activeElement);
                    if (pos && pos === Node.DOCUMENT_POSITION_PRECEDING) {
                        focus(lastFocusable);
                    }
                    else {
                        focus(firstFocusable);
                    }
                }
            });
        };
        if (previousState.isVisible !== isVisible) {
            if (isVisible) {
                // open tooltip
                if (isMobile)
                    document.body.classList.add("overlay-visible");
                document.getElementsByTagName("html")[0].classList.add('popup_visible', `popup_visible_${id}`);
                document.addEventListener('mousedown', handleClickOutside);
                document.addEventListener("keydown", onEsc, false);
                tooltipRef.current?.addEventListener('focusout', onFocusOut, false);
                if (tooltipInnerRef.current) {
                    const isOverflowing = tooltipInnerRef.current.scrollHeight > tooltipInnerRef.current.offsetHeight;
                    if (isOverflowing)
                        tooltipInnerRef.current.setAttribute("tabIndex", "0");
                    else
                        tooltipInnerRef.current.removeAttribute("tabIndex");
                }
                const focusable = document.querySelector(`#${id}_tooltip.tooltip`)?.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
                if (focusable && focusable.length > 0) {
                    firstFocusable = focusable[0];
                    lastFocusable = focusable[focusable.length - 1];
                }
                focus(tooltipRef.current, 200);
            }
            else {
                // close tooltip
                document.body.classList.remove("overlay-visible");
                document.getElementsByTagName("html")[0].classList.remove('popup_visible', `popup_visible_${id}`);
                focus(triggerRef.current, 0);
            }
        }
        return () => {
            previousState.isVisible = isVisible;
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener("keydown", onEsc, false);
            tooltipRef.current?.removeEventListener('focusout', onFocusOut, false);
            firstFocusable = null;
            lastFocusable = null;
        };
    }, [isVisible, id, isMobile, previousState]);
    return (React.createElement(React.Fragment, null,
        React.createElement("button", { type: "button", id: `${id}_tooltip-toggle`, className: `tooltip-trigger ${white ? "tooltip-white" : ""}`, "data-id": id, "data-dig-id": dataDigId, onClick: () => setVisible(true), ref: triggerRef },
            toggleContent,
            React.createElement("span", { className: "trigger-icon" },
                React.createElement("span", { className: "offscreen" }, lang === "fr" ? "info-bulle" : "tooltip"))),
        createPortal(React.createElement("div", { id: `${id}_tooltip_wrapper`, className: "popup_wrapper tooltipwpr" + (isVisible ? " popup_wrapper_visible" : ""), style: { ...tooltipWrapperStyle, ...posStyle }, ref: tooltipWprRef },
            React.createElement("div", { className: "tooltip_overlay", style: overlayStyle, onClick: () => setVisible(false) }),
            React.createElement("div", { id: `${id}_tooltip`, className: `tooltip ${dataH}${dataV} popup_content`, "data-h": dataH, "data-v": dataV, "data-popup-initialized": "true", role: "dialog", "aria-labelledby": `${id}_tooltip-toggle`, tabIndex: -1, "aria-hidden": !isVisible, ref: tooltipRef, style: tooltipStyle },
                React.createElement("div", { id: `${id}_tooltip-content`, className: "tooltip-inner", ref: tooltipInnerRef }, children),
                React.createElement("button", { className: "close", onClick: () => setVisible(false) },
                    React.createElement("span", null, lang === "fr" ? "Fermer" : "Close"))),
            React.createElement("span", { tabIndex: 0 })), document.body)));
};
export default DVLTooltip;
export const DVLTooltipWpr = ({ children, srHideText }) => {
    const childArray = React.Children.toArray(children);
    if (childArray.length > 1 && typeof childArray[0] === 'string') {
        const text = childArray[0];
        const lastSpaceIndex = text.lastIndexOf(" ");
        const front = text.substring(0, lastSpaceIndex);
        const back = "\u00A0" + text.substring(lastSpaceIndex + 1, text.length);
        const hideText = srHideText || false;
        return (React.createElement(React.Fragment, null,
            React.createElement("span", { className: "display-inline", "aria-hidden": hideText },
                front,
                React.createElement("span", { className: "offscreen" }, back)),
            React.createElement("span", { className: "no-wrap" },
                React.createElement("span", { "aria-hidden": "true" }, back),
                childArray[1])));
    }
    else {
        return React.createElement(React.Fragment, null, children);
    }
};
